import { getArchivedJobTimelineDate } from './displayUtils';
import { getWorkerName } from './workerUtils';

export const formatWorkOrderDateTime = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('sr-RS');
};

export const formatWorkOrderDuration = (seconds) => {
  const total = Number(seconds) || 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const getWorkOrderInterventionDate = (job) => getArchivedJobTimelineDate(job);

export const getWorkOrderTravelSeconds = (job, overrideSeconds) => {
  if (overrideSeconds != null && overrideSeconds !== '') {
    return Number(overrideSeconds) || 0;
  }
  return Number(job?.report?.totalTravelTime ?? job?.accumulatedTravelTime) || 0;
};

export const getWorkOrderWorkSeconds = (job, overrideSeconds) => {
  if (overrideSeconds != null && overrideSeconds !== '') {
    return Number(overrideSeconds) || 0;
  }
  return Number(job?.report?.totalWorkTime ?? job?.accumulatedWorkTime) || 0;
};

const pickCompanyField = (sources, keys) => {
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const value = source[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
  }
  return '';
};

const getCompanyProfileSources = (user) => {
  let profile = null;
  if (typeof window !== 'undefined') {
    try {
      const profileStr = localStorage.getItem('profile');
      if (profileStr) profile = JSON.parse(profileStr);
    } catch (e) {
      profile = null;
    }
  }
  return [
    user?.result?.company,
    profile?.result?.company,
    user?.result,
    user,
    profile?.result,
    profile
  ];
};

export const resolveWorkOrderCompanyName = (user, storedName) => {
  const fromUser = pickCompanyField(getCompanyProfileSources(user), ['companyName']);
  if (fromUser) return fromUser;
  if (storedName && storedName !== 'Vaša Kompanija') {
    return String(storedName).trim();
  }
  return '';
};

export const resolveWorkOrderCompanyDetails = (user, storedName) => {
  const sources = getCompanyProfileSources(user);
  const emailRaw = pickCompanyField(sources, ['email']);
  const email = emailRaw && !/no-email@example\.com/i.test(emailRaw) ? emailRaw : '';
  return {
    name: resolveWorkOrderCompanyName(user, storedName),
    phone: pickCompanyField(sources, ['phone']),
    email,
    address: pickCompanyField(sources, ['address']),
    city: pickCompanyField(sources, ['city']),
    pib: pickCompanyField(sources, ['pib']),
    registrationNumber: pickCompanyField(sources, ['registrationNumber']),
    bankAccount: pickCompanyField(sources, ['bankAccount']),
    bankName: pickCompanyField(sources, ['bankName']),
    website: pickCompanyField(sources, ['website'])
  };
};

export const formatWorkOrderNumber = (job) => {
  if (!job) return '';
  const number = job.jobNumber || job._id?.toString().slice(-6) || '';
  return number ? `#${number}` : '';
};

export const resolveWorkOrderWorkerName = (job, workers = [], partners = []) => {
  if (!job) return '';
  const assigned = getWorkerName(job.assignedTo, workers, partners);
  const invalidNames = ['Nedodeljeno', 'Nepoznat radnik', 'Nepoznat partner', '-'];
  if (assigned && !invalidNames.includes(assigned)) {
    return assigned;
  }
  if (job.lastUpdatedByName) return job.lastUpdatedByName;
  if (job.createdByName) return job.createdByName;
  return '';
};

const roundWorkOrderMoney = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

/** Standardne PDV stope po zemlji — korisnik i dalje može da unese svoju. */
export const DEFAULT_VAT_RATE_BY_COUNTRY = {
  rs: 20,
  ba: 17,
  me: 21,
  hr: 25,
  mk: 18,
  si: 22,
  al: 20,
  xk: 18,
  bg: 20,
  ro: 19,
  hu: 27,
  de: 19,
  at: 20,
  it: 22,
  gr: 24,
  pl: 23,
  cz: 21,
  sk: 20
};

export const getDefaultVatRate = (countryCode) => {
  const code = String(countryCode || '').toLowerCase().trim();
  return DEFAULT_VAT_RATE_BY_COUNTRY[code] ?? 20;
};

export const parseVatRateInput = (raw) => {
  if (raw == null || raw === '') return 0;
  const n = parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return 0;
  return n > 100 ? 100 : n;
};

/**
 * Izdvaja PDV iz iznosa u koj je porez već uračunat (bruto).
 * net = gross / (1 + rate/100), vat = gross - net
 */
export const extractVatFromGross = (gross, vatRatePercent) => {
  const amount = roundWorkOrderMoney(gross);
  const rate = parseVatRateInput(vatRatePercent);
  if (amount <= 0 || rate <= 0) {
    return { gross: amount, net: amount, vat: 0, rate };
  }
  const vat = roundWorkOrderMoney(amount * (rate / (100 + rate)));
  const net = roundWorkOrderMoney(amount - vat);
  return { gross: amount, net, vat, rate };
};

export const formatWorkOrderMoney = (value) =>
  `${roundWorkOrderMoney(value).toLocaleString('sr-RS', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} RSD`;

export const getSparePartLineGross = (part) =>
  roundWorkOrderMoney((Number(part?.price) || 0) * (Number(part?.quantity) || 0));

export const DEFAULT_PRINT_FIELD_VISIBILITY = {
  client: true,
  address: true,
  contact: true,
  device: true,
  issue: true,
  workDescription: true,
  usedSpareParts: true,
  materialsUsed: true,
  serviceCharged: true,
  costSummary: true,
  amortization: true,
  additionalNotes: true,
  times: true,
  vat: false,
  bankAccount: false
};
